import { useState } from 'react';
import {
  altaManualVisitaSoftys, anularVisitaSoftys, corregirVisitaSoftys, UNIDADES_PEDIDO,
  type PuestoRegistrado, type TipoVisitaSoftys, type UnidadPedido,
} from '../../../api/softys';
import { CampoCorregible } from '../../../components/CampoCorregible';
import {
  CONFIG_TOALLITAS, SIN_DINAMICA, type ConfigVisitasPuesto,
} from '../../../domain/softysConfig';

/**
 * Corregir, anular o cargar a mano un puesto de la campaña de Softys.
 *
 * ## Por qué un solo modal para las tres cosas
 *
 * Los campos son los mismos y las reglas también —motivo obligatorio, rastro en la misma
 * transacción, el tipo decide qué se pregunta—. Tres modales separados se verían distintos en
 * tres meses, que es exactamente lo que este proyecto ya pagó con las tarjetas de indicador.
 *
 * ## Corregir marca lo que cambia; cargar a mano no
 *
 * En **corrección** cada campo lleva su casilla «cambiar» y solo viaja lo marcado. Sin eso, abrir
 * el modal y guardar sin tocar nada escribiría los nueve campos en el rastro como si se hubieran
 * corregido los nueve: el historial diría que alguien reescribió la visita entera cuando lo único
 * que quiso fue arreglar un DNI.
 *
 * En **alta manual** no hay casillas: no hay valor anterior contra el cual marcar nada.
 *
 * ⚠️ **El motivo es obligatorio en las tres, y lo exige el servidor.** Acá se pide antes para no
 * hacer viajar un rechazo, pero la validación de verdad está en la función: una corrección sin
 * motivo es indistinguible de un accidente.
 */

type Modo = 'editar' | 'alta';
type Campo =
  | 'puesto_numero' | 'puesto_nombre' | 'comerciante_dni' | 'premio' | 'ocurrido_en'
  | 'hizo_pedido' | 'pedido_unidad' | 'pedido_cantidad' | 'escucho_capacitacion' | 'promociones'
  // Viven en `ficha` (0334/0353): solo las campañas que los preguntan, y solo al corregir.
  | 'dinamica' | 'muestra_entregada' | 'bonificacion_packs';

/** Los que el servidor NO deja vaciar. Es la misma lista que hace rebotar la RPC. */
const OBLIGATORIOS: ReadonlySet<Campo> = new Set<Campo>(['ocurrido_en']);

const hhmm = (iso: string): string => {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export default function RegistroSoftysModal({
  modo, tipo, registro, assignmentId, config = CONFIG_TOALLITAS, sujeto, onCerrar, onListo,
}: {
  modo: Modo;
  tipo: TipoVisitaSoftys;
  /** Solo en modo editar. */
  registro?: PuestoRegistrado;
  /** Solo en modo alta: la jornada a la que se cuelga. */
  assignmentId?: string;
  /** Premios, dinámicas y preguntas de ESTA campaña (0334/0353). Sin el dato, los de Toallitas. */
  config?: ConfigVisitasPuesto;
  /** «Mercado Belén · 14/09», para que el modal diga sobre qué se está escribiendo. */
  sujeto: string;
  onCerrar: () => void;
  onListo: (mensaje: string) => void;
}) {
  const esVenta = tipo === 'venta';
  const r = registro;

  const [marcados, setMarcados] = useState<Set<Campo>>(new Set());
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const [puestoNumero, setPuestoNumero] = useState(r?.puestoNumero ?? '');
  const [puestoNombre, setPuestoNombre] = useState(r?.puestoNombre ?? '');
  const [dni, setDni] = useState(r?.comercianteDni ?? '');
  const [premio, setPremio] = useState(r?.premio ?? '');
  const [hora, setHora] = useState(r ? hhmm(r.ocurridoEn) : '');
  const [hizoPedido, setHizoPedido] = useState<'' | 'si' | 'no'>(
    r?.hizoPedido === true ? 'si' : r?.hizoPedido === false ? 'no' : '',
  );
  const [pedidoUnidad, setPedidoUnidad] = useState<'' | UnidadPedido>(
    (r?.pedidoUnidad as UnidadPedido | null) ?? '',
  );
  const [pedidoCantidad, setPedidoCantidad] = useState(
    r?.pedidoCantidad === null || r?.pedidoCantidad === undefined ? '' : String(r.pedidoCantidad),
  );
  const [escucho, setEscucho] = useState<'' | 'si' | 'no'>(
    r?.escuchoCapacitacion === true ? 'si' : r?.escuchoCapacitacion === false ? 'no' : '',
  );
  const [promos, setPromos] = useState(
    r?.promociones === null || r?.promociones === undefined ? '' : String(r.promociones),
  );
  const premios = config.premios;
  const [dinamica, setDinamica] = useState(r?.dinamicaMayorista ?? '');
  const [muestra, setMuestra] = useState<'' | 'si' | 'no'>(
    r?.muestraEntregada === true ? 'si' : r?.muestraEntregada === false ? 'no' : '',
  );
  const [bonif, setBonif] = useState(
    r?.bonificacionPacks === null || r?.bonificacionPacks === undefined ? '' : String(r.bonificacionPacks),
  );
  /* Los tres de `ficha` se corrigen y NO se cargan a mano: el alta manual no los guarda. Se muestran si la campaña
     los pregunta o si el registro ya trae el dato. */
  const verDinamica = modo === 'editar'
    && (config.dinamicasMayorista.length > 0 || (r?.dinamicaMayorista ?? null) !== null);
  const verMuestra = modo === 'editar' && (config.preguntaMuestra || (r?.muestraEntregada ?? null) !== null);
  const verBonif = modo === 'editar'
    && (config.preguntaBonificacion || (r?.bonificacionPacks ?? null) !== null);
  const opcionesDinamica = [...config.dinamicasMayorista, SIN_DINAMICA];

  const alternar = (campo: Campo, marcar: boolean) => {
    setMarcados((antes) => {
      const s = new Set(antes);
      if (marcar) s.add(campo); else s.delete(campo);
      return s;
    });
  };
  const sePuedeVaciar = (campo: Campo) => !OBLIGATORIOS.has(campo);

  /** Solo lo MARCADO viaja. En alta manual no hay marcas y viaja todo lo que tenga valor. */
  function cambiosMarcados(): Record<string, string | null> {
    const todo: Partial<Record<Campo, string>> = {
      puesto_numero: puestoNumero,
      puesto_nombre: puestoNombre,
      comerciante_dni: dni,
      premio,
      // La fecha no se toca —define la jornada—, así que se reescribe la hora sobre el mismo día.
      ocurrido_en: hora === '' || r === undefined
        ? ''
        : new Date(`${r.ocurridoEn.slice(0, 10)}T${hora}:00`).toISOString(),
      hizo_pedido: hizoPedido === '' ? '' : String(hizoPedido === 'si'),
      pedido_unidad: pedidoUnidad,
      pedido_cantidad: pedidoCantidad,
      escucho_capacitacion: escucho === '' ? '' : String(escucho === 'si'),
      promociones: promos,
      dinamica,
      muestra_entregada: muestra === '' ? '' : String(muestra === 'si'),
      bonificacion_packs: bonif,
    };
    const out: Record<string, string | null> = {};
    for (const campo of marcados) {
      const v = todo[campo] ?? '';
      out[campo] = v === '' ? null : v;
    }
    return out;
  }

  async function guardar() {
    setError('');
    if (motivo.trim() === '') { setError('Falta el motivo.'); return; }

    if (modo === 'editar') {
      if (marcados.size === 0) {
        setError('No marcaste ningún campo para cambiar.');
        return;
      }
      setGuardando(true);
      try {
        await corregirVisitaSoftys(r!.id, tipo, cambiosMarcados(), motivo.trim());
        onListo(`Puesto ${r!.puestoNumero ?? ''} corregido.`);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'No se pudo corregir.');
      } finally { setGuardando(false); }
      return;
    }

    if (puestoNumero.trim() === '') { setError('Falta el número del puesto.'); return; }
    setGuardando(true);
    try {
      await altaManualVisitaSoftys({
        assignmentId: assignmentId!,
        tipo,
        puestoNumero: puestoNumero.trim(),
        puestoNombre: puestoNombre.trim() || null,
        comercianteDni: dni.trim() || null,
        premio: premio || null,
        hizoPedido: hizoPedido === '' ? null : hizoPedido === 'si',
        pedidoUnidad: pedidoUnidad === '' ? null : pedidoUnidad,
        pedidoCantidad: pedidoCantidad.trim() === '' ? null : Number(pedidoCantidad),
        escuchoCapacitacion: escucho === '' ? null : escucho === 'si',
        promociones: promos.trim() === '' ? null : Number(promos),
        hora: hora || null,
        motivo: motivo.trim(),
      });
      onListo(`Puesto ${puestoNumero.trim()} cargado a mano.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar.');
    } finally { setGuardando(false); }
  }

  async function anular() {
    setError('');
    if (motivo.trim() === '') { setError('Para anular hace falta un motivo.'); return; }
    setGuardando(true);
    try {
      await anularVisitaSoftys(r!.id, tipo, motivo.trim());
      onListo(`Puesto ${r!.puestoNumero ?? ''} anulado.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo anular.');
    } finally { setGuardando(false); }
  }

  const campo = 'mt-1 block w-full rounded border px-2 py-1.5 text-sm';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div role="dialog" aria-modal="true"
        aria-label={modo === 'editar' ? 'Corregir el registro' : 'Cargar un registro a mano'}
        className="w-full max-w-2xl rounded-lg border border-gray-300 bg-white shadow-lg">
        <header className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-gray-900">
              {modo === 'editar' ? 'Corregir el registro' : 'Cargar un registro a mano'}
              {' · '}{esVenta ? 'venta' : 'visita a comerciante'}
            </h3>
            <p className="truncate text-xs text-gray-600">{sujeto}</p>
          </div>
          <button type="button" onClick={onCerrar}
            className="shrink-0 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50">
            Cerrar
          </button>
        </header>

        <div className="space-y-4 px-4 py-4">
          {modo === 'editar' && (
            <p className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
              Marca <b>solo lo que vas a cambiar</b>. Lo que no marques queda deshabilitado
              mostrando lo que dice hoy el registro, y no viaja al servidor.
            </p>
          )}
          {modo === 'alta' && (
            <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Este registro va a quedar marcado como <b>cargado a mano</b> para siempre, con tu
              nombre. Si no pones la hora, se sella el mediodía y queda marcada como aproximada —
              esas filas no entran a ninguna cuenta por hora.
            </p>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <CampoCorregible etiqueta="Número del puesto" campo="puesto_numero" marcados={marcados}
              onAlternar={alternar} sePuedeVaciar={sePuedeVaciar}>
              <input value={puestoNumero} onChange={(e) => setPuestoNumero(e.target.value)}
                disabled={modo === 'editar' && !marcados.has('puesto_numero')}
                className={campo} />
            </CampoCorregible>

            <CampoCorregible etiqueta="Nombre o seña del puesto" campo="puesto_nombre"
              marcados={marcados} onAlternar={alternar} sePuedeVaciar={sePuedeVaciar}>
              <input value={puestoNombre} onChange={(e) => setPuestoNombre(e.target.value)}
                disabled={modo === 'editar' && !marcados.has('puesto_nombre')}
                className={campo} />
            </CampoCorregible>

            <CampoCorregible etiqueta="DNI del comerciante" campo="comerciante_dni"
              marcados={marcados} onAlternar={alternar} sePuedeVaciar={sePuedeVaciar}
              ayuda="Es el documento de un tercero: no se pinta en la tabla, solo acá.">
              <input value={dni} onChange={(e) => setDni(e.target.value)}
                disabled={modo === 'editar' && !marcados.has('comerciante_dni')}
                className={campo} inputMode="numeric" />
            </CampoCorregible>

            <CampoCorregible etiqueta="Hora de la visita" campo="ocurrido_en" marcados={marcados}
              onAlternar={alternar} sePuedeVaciar={sePuedeVaciar}>
              <input type="time" value={hora} onChange={(e) => setHora(e.target.value)}
                disabled={modo === 'editar' && !marcados.has('ocurrido_en')}
                className={campo} />
            </CampoCorregible>

            <CampoCorregible etiqueta="Premio de la ruleta" campo="premio" marcados={marcados}
              onAlternar={alternar} sePuedeVaciar={sePuedeVaciar}>
              <select value={premio} onChange={(e) => setPremio(e.target.value)}
                disabled={modo === 'editar' && !marcados.has('premio')} className={campo}>
                <option value="">— no giró —</option>
                {/* Un premio guardado que la lista ya no trae se sigue mostrando: si no, el
                    select lo pintaría como «no giró» y guardar sin tocarlo lo borraría. */}
                {premio !== '' && !premios.includes(premio) && <option value={premio}>{premio}</option>}
                {premios.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </CampoCorregible>

            {esVenta ? (
              <CampoCorregible etiqueta="Promociones compradas" campo="promociones"
                marcados={marcados} onAlternar={alternar} sePuedeVaciar={sePuedeVaciar}>
                <input type="number" min={0} value={promos}
                  onChange={(e) => setPromos(e.target.value)}
                  disabled={modo === 'editar' && !marcados.has('promociones')} className={campo} />
              </CampoCorregible>
            ) : (
              <>
                <CampoCorregible etiqueta="¿Hizo pedido?" campo="hizo_pedido" marcados={marcados}
                  onAlternar={alternar} sePuedeVaciar={sePuedeVaciar}>
                  <select value={hizoPedido}
                    onChange={(e) => setHizoPedido(e.target.value as '' | 'si' | 'no')}
                    disabled={modo === 'editar' && !marcados.has('hizo_pedido')} className={campo}>
                    <option value="">— sin respuesta —</option>
                    <option value="si">Sí</option>
                    <option value="no">No</option>
                  </select>
                </CampoCorregible>

                <CampoCorregible etiqueta="Unidad del pedido" campo="pedido_unidad"
                  marcados={marcados} onAlternar={alternar} sePuedeVaciar={sePuedeVaciar}>
                  <select value={pedidoUnidad}
                    onChange={(e) => setPedidoUnidad(e.target.value as '' | UnidadPedido)}
                    disabled={modo === 'editar' && !marcados.has('pedido_unidad')} className={campo}>
                    <option value="">—</option>
                    {UNIDADES_PEDIDO.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </CampoCorregible>

                <CampoCorregible etiqueta="Cantidad pedida" campo="pedido_cantidad"
                  marcados={marcados} onAlternar={alternar} sePuedeVaciar={sePuedeVaciar}>
                  <input type="number" min={0} value={pedidoCantidad}
                    onChange={(e) => setPedidoCantidad(e.target.value)}
                    disabled={modo === 'editar' && !marcados.has('pedido_cantidad')}
                    className={campo} />
                </CampoCorregible>

                <CampoCorregible etiqueta="¿Escuchó la capacitación?" campo="escucho_capacitacion"
                  marcados={marcados} onAlternar={alternar} sePuedeVaciar={sePuedeVaciar}>
                  <select value={escucho}
                    onChange={(e) => setEscucho(e.target.value as '' | 'si' | 'no')}
                    disabled={modo === 'editar' && !marcados.has('escucho_capacitacion')}
                    className={campo}>
                    <option value="">— sin respuesta —</option>
                    <option value="si">Sí</option>
                    <option value="no">No</option>
                  </select>
                </CampoCorregible>

                {verDinamica && (
                  <CampoCorregible etiqueta="Dinámica con la que compró" campo="dinamica"
                    marcados={marcados} onAlternar={alternar} sePuedeVaciar={sePuedeVaciar}
                    ayuda="Solo si hizo pedido. Si no se sabe, déjala vacía: «Sin dinámica» quiere decir que compró fuera de ella.">
                    <select value={dinamica} onChange={(e) => setDinamica(e.target.value)}
                      disabled={!marcados.has('dinamica')} className={campo}>
                      <option value="">— no informada —</option>
                      {dinamica !== '' && !opcionesDinamica.includes(dinamica)
                        && <option value={dinamica}>{dinamica}</option>}
                      {opcionesDinamica.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </CampoCorregible>
                )}

                {verMuestra && (
                  <CampoCorregible etiqueta="¿Recibió la muestra?" campo="muestra_entregada"
                    marcados={marcados} onAlternar={alternar} sePuedeVaciar={sePuedeVaciar}>
                    <select value={muestra} onChange={(e) => setMuestra(e.target.value as '' | 'si' | 'no')}
                      disabled={!marcados.has('muestra_entregada')} className={campo}>
                      <option value="">— sin respuesta —</option>
                      <option value="si">Sí</option>
                      <option value="no">No</option>
                    </select>
                  </CampoCorregible>
                )}

                {verBonif && (
                  <CampoCorregible etiqueta="Packs de regalo entregados" campo="bonificacion_packs"
                    marcados={marcados} onAlternar={alternar} sePuedeVaciar={sePuedeVaciar}>
                    <input type="number" min={0} value={bonif} onChange={(e) => setBonif(e.target.value)}
                      disabled={!marcados.has('bonificacion_packs')} className={campo} />
                  </CampoCorregible>
                )}
              </>
            )}
          </div>

          <label className="block text-sm">
            Motivo <span className="text-red-700">*</span>
            <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2}
              placeholder={modo === 'editar'
                ? 'Qué estaba mal y de dónde sale el dato correcto'
                : 'Por qué no se registró en campo'}
              className="mt-1 block w-full rounded border px-2 py-1.5 text-sm" />
            <span className="text-xs text-gray-500">
              Queda guardado con tu nombre. Sin motivo, esto es indistinguible de un accidente.
            </span>
          </label>

          {error !== '' && (
            <p role="alert" className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-900">
              {error}
            </p>
          )}
        </div>

        <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 px-4 py-3">
          {modo === 'editar' && r?.estado === 'registrado' ? (
            <button type="button" onClick={() => void anular()} disabled={guardando}
              className="rounded border border-red-300 px-3 py-1.5 text-sm text-red-800 disabled:opacity-50">
              Anular este registro
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button type="button" onClick={onCerrar}
              className="rounded border border-gray-300 px-3 py-1.5 text-sm">
              Cancelar
            </button>
            <button type="button" onClick={() => void guardar()} disabled={guardando}
              className="rounded bg-blue-600 px-4 py-1.5 text-sm text-white disabled:opacity-50">
              {guardando ? 'Guardando…' : (modo === 'editar' ? 'Guardar la corrección' : 'Cargar')}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
